import { countryOptions } from "../constants/countryOptions";
import { assignDatesToTabs } from "./date-time";
import { format } from "date-fns";

export const capitalizeFirstLetter = (str: string) => {
  return str?.charAt(0)?.toUpperCase() + str?.slice(1);
};

export const formatToTitleCase = (str: string) => {
  if (!str) return "";
  const spaced = str.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[-_]/g, " ");
  return spaced
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const formatLargeDisplayNumbers = (
  value: number
): string | undefined => {
  if (value === null || value === undefined || isNaN(value)) return undefined;

  const absValue = Math.abs(value);
  const suffixes = [
    { value: 1e12, suffix: "T" },
    { value: 1e9, suffix: "B" },
    { value: 1e6, suffix: "M" },
    { value: 1e3, suffix: "K" },
  ];

  for (const { value: divisor, suffix } of suffixes) {
    if (absValue >= divisor) {
      const formatted = (value / divisor).toFixed(2);
      const cleaned = formatted.replace(/\.?0+$/, "");
      return `${cleaned}${suffix}`;
    }
  }

  return `${value}`;
};

export const getCountryFlagFromDialCode = (dialCode: string): string => {
  if (!dialCode) return "";

  const formattedDialCode = dialCode.startsWith("+")
    ? dialCode
    : `+${dialCode}`;

  const country = countryOptions?.find(
    (option) => option.code === formattedDialCode
  );

  return country ? country?.flag : dialCode;
};

export const markdownBreakFormatter = (content: string) => {
  if (typeof content !== "string") {
    return "";
  }
  return (
    content?.replace(
      /\n+/g,
      (match) => "<br>".repeat(match?.length) + "\n".repeat(match?.length)
    ) || ""
  );
};

export const truncateItemsWithCount = (val: string, maxLength: number = 30) => {
  if (typeof val !== "string" || val?.length <= maxLength) return val;

  const items = val?.split(",")?.map((item) => item?.trim());
  if (items?.length <= 1) return val;

  const suffixReserve = 6;
  const effectiveMaxLength = maxLength - suffixReserve;

  const fitted = items?.reduce<{
    result: string;
    count: number;
    done: boolean;
  }>(
    (acc, item, index) => {
      if (acc?.done) return acc;

      const next = index === 0 ? item : `${acc?.result}, ${item}`;

      if (index === 0 && item?.length > effectiveMaxLength) {
        return {
          result: item?.substring(0, effectiveMaxLength - 3) + "...",
          count: 1,
          done: true,
        };
      }

      if (next?.length > effectiveMaxLength && index > 0) {
        return { ...acc, done: true };
      }

      return { result: next, count: index + 1, done: false };
    },
    { result: "", count: 0, done: false }
  );

  const remaining = items?.length - fitted?.count;
  return remaining > 0 ? `${fitted?.result}, +${remaining}` : fitted?.result;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
export const convertFiltersToQueryString = (filters: any): string => {
  const queryParams = new URLSearchParams();

  if (filters?.sort) {
    queryParams?.append("sort_by", filters?.sort);
  }

  if (filters?.country?.value) {
    queryParams?.append("country", `${filters?.country?.id}`);
  }

  if (filters?.city?.value) {
    queryParams?.append("city", `${filters?.city?.id}`);
  }

  if (filters?.university?.value) {
    queryParams?.append("university", `${filters?.university?.id}`);
  }

  if (filters?.room?.value) {
    queryParams?.append("room_kind", filters?.room?.value);
  }

  if (filters?.budget?.value) {
    const [min, max] = filters.budget.value.split("-");
    if (min) queryParams?.append("min_price", min);
    if (max) queryParams?.append("max_price", max);
  }

  return queryParams?.toString();
};

export const filterNonEmptyValues = (
  obj: Record<string, any>
): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) =>
        value !== null &&
        value !== undefined &&
        value !== "No" &&
        value !== "" &&
        (Array?.isArray(value) ? value?.length > 0 : true)
    )
  );
};

export const addTargetBlankToLinks = (html: string): string => {
  return html.replace(
    /<a /g,
    '<a target="_blank" rel="noopener noreferrer" class="text-blue-500! underline!" '
  );
};

export const convertPhoneNumbersToLinks = (html: string): string => {
  const phoneRegex =
    /(?<!href=["'][^"']*?)(?<!tel:)(\+?[\d]{1,4}[-.\s]?)?(\(?\d{2,4}\)?[-.\s]?)(\d{2,4}[-.\s]?){1,3}\d{2,4}(?![^<]*>)/g;

  return html.replace(phoneRegex, (match) => {
    const cleanNumber = match.replace(/[\s()-]/g, "");
    const digitCount = match.replace(/\D/g, "").length;
    if (digitCount >= 7 && digitCount <= 15) {
      return `<a href="tel:${cleanNumber}" class="text-blue-500! underline!">${match}</a>`;
    }
    return match;
  });
};

export const processHtmlContent = (htmlContent: string): string => {
  if (!htmlContent) return "";

  return htmlContent
    .replace(/<p[^>]*>\s*<\/p>(?!\s*$)/g, "<br>")
    .replace(/<p[^>]*>\s*<\/p>\s*$/g, "");
};

export const cleanObject = (obj: object) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) => value !== "" && value !== null && value !== undefined
    )
  );

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export const decodeFormattedCount = (
  formattedValue: string | number
): number => {
  if (typeof formattedValue === "number") return formattedValue;
  if (typeof formattedValue === "string" && formattedValue.endsWith("K")) {
    return parseInt(formattedValue.replace("K", ""), 10) * 1000;
  }
  return parseInt(formattedValue, 10) || 0;
};

export const getFilterDates = (page: string) => {
  const storedDates = window.sessionStorage.getItem(page);

  if (!storedDates) {
    const defaultDates = assignDatesToTabs("last-30-days");
    if (!defaultDates) {
      throw new Error("Failed to get default dates");
    }

    const newDateFilter = {
      date_start: format(defaultDates.date_start, "yyyy-MM-dd HH:mm:ss"),
      date_end: format(defaultDates.date_end, "yyyy-MM-dd HH:mm:ss"),
      selectedTab: {
        name: "Last 30 days",
        id: "last-30-days",
      },
    };

    window.sessionStorage.setItem(page, JSON.stringify(newDateFilter));
    return newDateFilter;
  }

  const parsedStoredDates: {
    selectedTab: {
      id: string;
      name: string;
    };
    date_start: string;
    date_end: string;
  } = JSON.parse(storedDates);

  const selectedTab = parsedStoredDates?.selectedTab?.id ?? "last-30-days";

  let date_start: Date;
  let date_end: Date;

  if (selectedTab === "custom") {
    const currentTime = new Date();
    const currentDate = currentTime.getDate();
    const currentMonth = currentTime.getMonth();
    const currentYear = currentTime.getFullYear();

    date_start = new Date(parsedStoredDates.date_start);
    date_start.setHours(0, 0, 0);

    date_end = new Date(parsedStoredDates.date_end);
    const isToday =
      date_end.getDate() === currentDate &&
      date_end.getMonth() === currentMonth &&
      date_end.getFullYear() === currentYear;

    if (isToday) {
      date_end.setHours(
        currentTime.getHours(),
        currentTime.getMinutes(),
        currentTime.getSeconds()
      );
    } else {
      date_end.setHours(23, 59, 59);
    }
  } else {
    const tabDates = assignDatesToTabs(selectedTab);
    if (tabDates) {
      date_start = tabDates.date_start;
      date_end = tabDates.date_end;
    } else {
      // Fallback to default
      const defaultDates = assignDatesToTabs("last-30-days");
      if (!defaultDates) {
        throw new Error("Failed to get fallback dates");
      }
      date_start = defaultDates.date_start;
      date_end = defaultDates.date_end;
    }
  }

  const newDateFilter = {
    selectedTab: {
      ...parsedStoredDates.selectedTab,
    },
    date_start: format(date_start, "yyyy-MM-dd HH:mm:ss"),
    date_end: format(date_end, "yyyy-MM-dd HH:mm:ss"),
  };

  window.sessionStorage.setItem(page, JSON.stringify(newDateFilter));
  return newDateFilter;
};
